import React, { useEffect, useState } from 'react';
import { getRacePicks, getDrivers, getResults, getCurrentRace, isRaceLocked, getRaceDnfs, getQuestions, getRaceDrivers } from '../api';
import scorePicks from '../utilties/scoring';
import { scorePicksBreakdown } from '../utilties/scoring';

export default function RaceSubmissions({ raceId, results: resultsProp }) {
    const [picks, setPicks] = useState([]);
    const [driversMap, setDriversMap] = useState({});
    const [driversCodeMap, setDriversCodeMap] = useState({});
    const [results, setResults] = useState([]);
    const [raceDnfs, setRaceDnfs] = useState([]);
    const [maskPicks, setMaskPicks] = useState(false);
    const [questions, setQuestions] = useState([]);
    const scorableResults = (results || []).slice(3, 10);

    useEffect(() => {
        if (!raceId) return;
        Promise.all([getRacePicks(raceId), getDrivers(), getResults(raceId), getRaceDnfs(raceId), getQuestions(raceId), getRaceDrivers(raceId)])
            .then(([pk, drv, res, dnfs, qs, rdResp]) => {
                // Use race-specific drivers if available, otherwise all drivers
                const driverList = (rdResp?.drivers && rdResp.drivers.length > 0) ? rdResp.drivers : drv;
                const map = {};
                const codeMap = {};
                (driverList || []).forEach((d) => {
                    const name = (d.name || `${d.first_name || ''} ${d.last_name || ''}`).trim();
                    map[d.id] = name;
                    codeMap[d.id] = d.code || '';
                });
                // Also include all drivers for code/name lookups (picks may reference drivers not in current lineup)
                (drv || []).forEach((d) => {
                    if (!map[d.id]) {
                        const name = (d.name || `${d.first_name || ''} ${d.last_name || ''}`).trim();
                        map[d.id] = name;
                        codeMap[d.id] = d.code || '';
                    }
                });
                setDriversMap(map);
                setDriversCodeMap(codeMap);
                setPicks(pk || []);
                setResults(res || []);
                setRaceDnfs(Array.isArray(dnfs) ? dnfs : []);
                setQuestions(qs || []);
            })
            .catch((e) => {
                console.error('RaceSubmissions load error', e);
                setPicks([]);
                setResults([]);
            });

        // compute masking: mask when this is current race and NOT locked
        Promise.all([getCurrentRace(), isRaceLocked(raceId)])
            .then(([cur, locked]) => {
                setMaskPicks(cur && String(cur) === String(raceId) && !locked);
            })
            .catch(() => setMaskPicks(false));
    }, [raceId]);

    // Helper to parse bonus_answers string into a map
    function parseBonusAnswers(str) {
        if (!str) return {};
        const map = {};
        str.split(',').forEach((pair) => {
            const [qId, ans] = pair.split(':');
            if (qId && ans) map[qId] = parseInt(ans);
        });
        return map;
    }

    // Helper to get the label for a user's answer to a question
    function getAnswerLabel(q, answerNum) {
        if (!answerNum) return '';
        if (answerNum === 1) return q.option_1 || '';
        if (answerNum === 2) return q.option_2 || '';
        if (answerNum === 3) return q.option_3 || '';
        if (answerNum === 4) return q.option_4 || '';
        return '';
    }

    function renderWithPoints(label, points) {
        return (
            <>
                {label}
                {/* {points > 0 ? (
                    <span className="ms-1 fst-italic small">
                        +{points}
                    </span>
                ) : null} */}
            </>
        );
    }

    return (
        <>
            <h4 className="mt-4">Submitted Picks (Positions 4-10 + DNF)</h4>
            <div className="table-responsive">
                <table className="table table-sm table-bordered">
                    <thead>
                        <tr>
                            <th>User</th>
                            {Array.from({ length: 7 }).map((_, i) => <th key={i}>#{i + 4}</th>)}
                            <th>DNF</th>
                            {questions.map((q, idx) => <th key={`q-${q.id}`}>Q{idx + 1}</th>)}
                            <th>BNS</th>
                            <th>Score</th>
                        </tr>
                    </thead>
                    <tbody>
                        {picks.map((p) => {
                            const userDnfId = p.dnf ? (p.dnf.id ?? p.dnf) : null;
                            const breakdown = scorePicksBreakdown(results, p.picks, raceDnfs, userDnfId, questions, p.bonus_answers);
                            return <tr key={p.user_id || p.username}>
                                <td><strong>{p.display_name || p.username}</strong></td>
                                {Array.from({ length: 7 }).map((_, i) => {
                                    const pickIndex = i + 3;
                                    const position = i + 4;
                                    const pick = p.picks[pickIndex];
                                    // Expect pick to be a driver object { id, name, code, ... } or null
                                    const pickId = pick ? (pick.id ?? pick) : null;
                                    const officialId = results[pickIndex];
                                    const points = breakdown.positionPoints[position] || 0;
                                    let style = {};
                                    if (!maskPicks && pickId && points > 0) {
                                        style = points === 3 ? { backgroundColor: '#004911' } : { backgroundColor: '#3b3f00' };
                                    }
                                    const pickLabel = pick?.code || driversCodeMap[pickId] || '—';
                                    return <td key={i} style={style}>{maskPicks ? '****' : renderWithPoints(pickLabel, points)}</td>;
                                })}
                                {(() => {
                                    if (maskPicks) return <td>****</td>;
                                    let dnfStyle = {};
                                    if (userDnfId === null) {
                                        // User picked No DNF — highlight yellow if there were no DNFs
                                        if (raceDnfs.length === 0 && results.length > 0) {
                                            dnfStyle = { backgroundColor: '#3b3f00' };
                                        }
                                    } else if (raceDnfs.includes(Number(userDnfId))) {
                                        // User picked a driver who actually DNF'd — highlight green
                                        dnfStyle = { backgroundColor: '#004911' };
                                    }
                                    const dnfLabel = userDnfId ? (p.dnf?.code || driversCodeMap[userDnfId] || '') : 'No DNF';
                                    return <td style={dnfStyle}>{renderWithPoints(dnfLabel, breakdown.dnfPoints || 0)}</td>;
                                })()}
                                {/* Bonus question cells */}
                                {questions.map((q) => {
                                    if (maskPicks) return <td key={`q-${q.id}`}>****</td>;
                                    const userAnswers = parseBonusAnswers(p.bonus_answers);
                                    const userAns = userAnswers[String(q.id)];
                                    const label = getAnswerLabel(q, userAns);
                                    let qStyle = {};
                                    if (q.correct_answer && userAns === q.correct_answer) {
                                        qStyle = { backgroundColor: '#004911' };
                                    }
                                    const qPts = breakdown.questionPoints[String(q.id)] || 0;
                                    return <td key={`q-${q.id}`} style={qStyle}>{renderWithPoints(label || '—', qPts)}</td>;
                                })}
                                <td>{maskPicks ? '****' : (breakdown.bonusPoints || 0)}</td>
                                <td>{maskPicks ? '****' : scorePicks(results, p.picks, raceDnfs, userDnfId, questions, p.bonus_answers)}</td>
                            </tr>;
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
}
